import React, { useState, useEffect } from 'react';
import { 
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from '@/components/ui/carousel';
import { ImageLoader } from './ImageLoader';
import { ServiceImagePlaceholder } from '@/components/shared/ServiceImagePlaceholder';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useDebouncedCallback } from 'use-debounce';
import { ServiceImage } from '@/types';
import type { CarouselApi } from "@/components/ui/carousel"

interface ServiceImageSectionProps {
  serviceImages: any[];
  serviceName: string;
  loadedImages: {[key: string]: boolean};
  handleImageLoad: (imageId: string) => void;
  current: number;
  setCarouselApi: (api: any) => void;
  singleImageUrl?: string;
  hasMultipleImages: boolean;
  isLoading?: boolean;
  context?: 'customer' | 'admin';
}

export const ServiceImageSection: React.FC<ServiceImageSectionProps> = ({
  serviceImages,
  serviceName,
  loadedImages,
  handleImageLoad,
  current,
  setCarouselApi,
  singleImageUrl,
  hasMultipleImages,
  isLoading = false,
  context = 'customer'
}) => {
  // Add local reference to carouselApi for the event handlers
  const [localCarouselApi, setLocalCarouselApi] = useState<CarouselApi | null>(null);
  
  // Update both APIs when carousel is initialized
  const handleSetApi = (api: CarouselApi) => {
    setLocalCarouselApi(api);
    setCarouselApi(api);
  };
  
  console.log('ServiceImageSection render:', {
    serviceName,
    serviceImages,
    hasMultipleImages,
    singleImageUrl,
    current,
    loadedImages,
    context
  });

  // Check if we have any images to display
  const hasImages = (serviceImages && serviceImages.length > 0 && serviceImages.some(img => img.image_url && img.image_url.trim() !== '')) || 
                    (singleImageUrl && singleImageUrl.trim() !== '');
                    
  // If no images, return null
  if (!hasImages) {
    console.log('No images to display');
    return null;
  }
  
  // Use single image URL if serviceImages is empty
  const useSingleImage = !hasMultipleImages && singleImageUrl && singleImageUrl.trim() !== '';
  
  console.log('Rendering mode:', {
    useSingleImage,
    hasMultipleImages,
    imageCount: serviceImages?.length,
    hasImages,
    context
  });

  // For admin context, just show a static image
  if (context === 'admin') {
    console.log('Rendering single image (admin context)');
    const image = serviceImages && serviceImages.length > 0 ? serviceImages[0] : null;
    const imageUrl = image ? image.image_url : singleImageUrl;
    const imageId = image ? 
      (image.id || `${image.image_url || 'image-0'}-0`) : 
      (singleImageUrl || `single-image-${serviceName}`);
    
    return (
      <div className="w-full h-full relative">
        <ImageLoader 
          imageId={imageId}
          imageUrl={imageUrl || ''}
          altText={serviceName}
          isLoaded={loadedImages[imageId] || false}
          onLoad={handleImageLoad}
          showPlaceholder={true}
        />
      </div>
    );
  }

  // For customer context, always use a carousel if we have at least one image
  if (context === 'customer') {
    // If there's only a single image URL and no service images
    if ((!serviceImages || serviceImages.length === 0) && singleImageUrl && singleImageUrl.trim() !== '') {
      console.log('Rendering single image (customer context, but only singleImageUrl available)');
      return (
        <div className="w-full h-full relative">
          <ImageLoader 
            imageId={singleImageUrl || `single-image-${serviceName}`}
            imageUrl={singleImageUrl || ''}
            altText={serviceName}
            isLoaded={loadedImages[singleImageUrl || `single-image-${serviceName}`] || false}
            onLoad={handleImageLoad}
            showPlaceholder={true}
          />
        </div>
      );
    }
    
    // For customer context with images, always use a carousel (even for single image)
    console.log('Rendering carousel with images (customer context):', {
      serviceImagesCount: serviceImages?.length,
      imageUrls: serviceImages?.map(img => img.image_url),
      serviceName,
      current
    });
    
    // Make sure we have valid image URLs before rendering
    const validImages = serviceImages.filter(img => img && img.image_url && img.image_url.trim() !== '');
    console.log(`Found ${validImages.length} valid images for carousel`);
    
    if (validImages.length === 0) {
      console.log('No valid images available for carousel');
      return null;
    }

    // Register a manual click handler for the navigation buttons
    const handleNextClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (localCarouselApi) {
        const current = localCarouselApi.selectedScrollSnap();
        const next = (current + 1) % validImages.length;
        console.log(`Manual next navigation: ${current} -> ${next}`);
        // Force a reset to the first slide when at the end
        if (current === validImages.length - 1) {
          localCarouselApi.scrollTo(0);
        } else {
          localCarouselApi.scrollNext();
        }
      }
    };

    const handlePrevClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (localCarouselApi) {
        const current = localCarouselApi.selectedScrollSnap();
        const prev = (current - 1 + validImages.length) % validImages.length;
        console.log(`Manual prev navigation: ${current} -> ${prev}`);
        // Force a reset to the last slide when at the beginning
        if (current === 0) {
          localCarouselApi.scrollTo(validImages.length - 1);
        } else {
          localCarouselApi.scrollPrev();
        }
      }
    };
    
    return (
      <Carousel
        opts={{
          align: "start",
          loop: true,
          containScroll: false,
          dragFree: false
        }}
        className="w-full h-full"
        onMouseDown={(e) => e.stopPropagation()}
        setApi={handleSetApi}
      >
        <CarouselContent className="h-full">
          {validImages.map((image, index) => {
            const imageId = image.id || `${image.image_url || `image-${index}`}-${index}`;
            console.log(`Rendering carousel item ${index}:`, {
              imageId,
              imageUrl: image.image_url,
              isLoaded: loadedImages[imageId] || false
            });
            
            return (
              <CarouselItem key={imageId} className="h-full">
                <div className="relative w-full h-full">
                  <ImageLoader 
                    imageId={imageId}
                    imageUrl={image.image_url || ''}
                    altText={serviceName}
                    isLoaded={loadedImages[imageId] || false}
                    onLoad={handleImageLoad}
                    showPlaceholder={true}
                  />
                </div>
              </CarouselItem>
            );
          })}
        </CarouselContent>
        
        {/* Always show navigation controls for customer context */}
        {validImages.length > 1 && (
          <>
            <CarouselPrevious 
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 border-0 shadow-lg w-10 h-10 rounded-full opacity-70 hover:opacity-100 transition-opacity duration-200 hidden sm:flex"
              onClick={handlePrevClick}
            >
              <ChevronLeft className="h-5 w-5" />
            </CarouselPrevious>
            <CarouselNext 
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 border-0 shadow-lg w-10 h-10 rounded-full opacity-70 hover:opacity-100 transition-opacity duration-200 hidden sm:flex"
              onClick={handleNextClick}
            >
              <ChevronRight className="h-5 w-5" />
            </CarouselNext>
            
            {/* Dots indicators - always show these on all devices */}
            <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1">
              {validImages.map((_, index) => (
                <button
                  key={index}
                  className={`w-2 h-2 rounded-full transition-all ${
                    current === index ? 'bg-white scale-125' : 'bg-white/50'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (localCarouselApi) {
                      console.log(`Dot navigation: ${current} -> ${index}`);
                      localCarouselApi.scrollTo(index);
                    }
                  }}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </Carousel>
    );
  }
  
  // If we get here, there are no valid images
  console.log('No valid images to display');
  return null;
}
